import { Chart, useChart } from "@chakra-ui/charts";
import { Cell, Label, Pie, PieChart, Tooltip } from "recharts";
import { useEffect, useState } from "react";
import api from "../../../lib/api";

interface RegionalDrugConsumptionProps {
  dateFilter: string;
  customRange?: { start: string; end: string };
  stateFilter?: string;
}

interface LocationData {
  state?: string;
  lga?: string;
  transactions: number;
  revenue: number;
  total_quantity: number;
}

const COLORS = [
  "blue.solid",
  "orange.solid",
  "pink.solid",
  "green.solid",
  "purple.solid",
  "teal.solid",
  "red.solid",
];

export const RegionalDrugConsumption = ({ dateFilter, customRange, stateFilter }: RegionalDrugConsumptionProps) => {
  const [loading, setLoading] = useState(false);
  const [locationData, setLocationData] = useState<LocationData[]>([]);

  useEffect(() => {
    fetchLocationData();
  }, [dateFilter, customRange?.start, customRange?.end, stateFilter]);

  const getDateRange = (filter: string) => {
    if (filter === "custom" && customRange?.start && customRange?.end) {
        return { start_date: customRange.start, end_date: customRange.end };
    }

    const end = new Date();
    const start = new Date();

    switch (filter) {
      case "7d":
        start.setDate(end.getDate() - 7);
        break;
      case "30d":
        start.setDate(end.getDate() - 30);
        break;
      case "90d":
        start.setDate(end.getDate() - 90);
        break;
      case "180d":
        start.setDate(end.getDate() - 180);
        break;
      case "this-month":
        start.setDate(1);
        break;
      case "last-month":
        start.setMonth(end.getMonth() - 1);
        start.setDate(1);
        end.setDate(0);
        break;
      default:
        start.setDate(end.getDate() - 30);
    }

    return {
      start_date: start.toISOString().split("T")[0],
      end_date: end.toISOString().split("T")[0],
    };
  };

  const fetchLocationData = async () => {
    setLoading(true);
    try {
      const { start_date, end_date } = getDateRange(dateFilter);
      const params: any = { start_date, end_date, limit: 7 };
      
      // If state filter is selected, fetch LGA data instead of state data
      let endpoint = "/analytics/sales/by-state";
      if (stateFilter && stateFilter.trim() !== "") {
        endpoint = "/analytics/sales/by-lga";
        params.state = stateFilter;
      }

      const response = await api.get(endpoint, { params });
      
      // Handle both states and lgas response formats
      if (response.data?.data?.states) {
          setLocationData(response.data.data.states);
      } else if (response.data?.data?.lgas) {
          setLocationData(response.data.data.lgas);
      } else {
          setLocationData([]);
      }
    } catch (error) {
      console.error("Failed to fetch location data:", error);
      setLocationData([]);
    } finally {
      setLoading(false);
    }
  };

  const chartData = locationData.map((item, index) => ({
    name: item.state || item.lga || "Unknown",
    value: item.transactions,
    color: COLORS[index % COLORS.length],
  }));

  const chart = useChart({
    data: chartData,
  });

  return (
    <div className="bg-white p-4! rounded-2xl shadow">
      <div className="flex items-center justify-between mb-12!">
        <p className="text-sm! font-semibold!">
          {stateFilter && stateFilter.trim() !== "" 
            ? `Top 7 LGAs in ${stateFilter}` 
            : "Top 7 Regional Drug Consumption"}
        </p>
      </div>
      
      <div className="flex justify-center">
        {!loading && chartData.length === 0 ? (
           <div className="flex h-[300px] w-full items-center justify-center text-gray-500 text-sm italic border border-dashed border-gray-200 rounded-lg">
             No regional data available for this period
           </div>
        ) : (
        <Chart.Root height="300px" width="100%" chart={chart}>
          <PieChart>
            <Pie
              data={chartData}
              innerRadius={80}
              outerRadius={120}
              paddingAngle={5}
              dataKey="value"
            >
              <Label
                value={locationData.reduce((acc, curr) => acc + curr.transactions, 0).toLocaleString()}
                position="center"
                className="text-lg font-bold"
                fill="black"
              />
               {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={chart.color(entry.color)} />
               ))}
            </Pie>
            <Tooltip 
                content={({ payload }) => {
                  if (!payload || payload.length === 0) return null;
                  const item = payload[0];
                  // Find the original data item to get revenue
                  const originalItem = locationData.find(s => (s.state || s.lga) === item.name);
                  return (
                    <div className="bg-white p-2 border rounded shadow text-sm">
                      <div className="font-bold">{item.name}</div>
                      <div>Txns: {item.value}</div>
                      {originalItem && <div>Revenue: ${originalItem.revenue.toLocaleString()}</div>}
                    </div>
                  );
                }}
            />
          </PieChart>
        </Chart.Root>
        )}
      </div>
      
      {/* Legend below chart */}
      <div className="mt-4 grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
        {chartData.map((entry, index) => (
            <div key={index} className="flex items-center text-xs">
                <div 
                    className="w-3 h-3 rounded-full mr-2" 
                    style={{ backgroundColor: chart.color(entry.color) }} // Use the color from theme
                ></div>
                <span className="truncate" title={entry.name}>
                    {entry.name} ({((entry.value / (locationData.reduce((a,b)=>a+b.transactions,0) || 1)) * 100).toFixed(1)}%)
                </span>
            </div>
        ))}
      </div>
    </div>
  );
};
